import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import Badge from '@atlaskit/badge/badge-new';
import IconButton from '@atlaskit/button/icon/button';
import DropdownMenu from '@atlaskit/dropdown-menu/dropdown-menu';
import DropdownItem from '@atlaskit/dropdown-menu/dropdown-menu-item';
import DropdownItemGroup from '@atlaskit/dropdown-menu/dropdown-menu-item-group';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/useSharedPluginStateWithSelector';
import DeleteIcon from '@atlaskit/icon/core/delete';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';

import {
	useFullPageEditorApiState,
	type FullPageEditorPresetAPI,
} from '@confluence/full-page-editor-store';

type AISuggestionsSharedState = ReturnType<
	NonNullable<FullPageEditorPresetAPI['aiSuggestions']>['sharedState']['currentState']
>;

const i18n = defineMessages({
	moreOptions: {
		id: 'suggestions-panel.header.more-options',
		defaultMessage: 'Suggestions options',
		description: 'Accessible label for the menu of actions for all AI suggestions',
	},
	reviewAgain: {
		id: 'suggestions-panel.header.review-again',
		defaultMessage: 'Review again',
		description: 'Menu item to review the document again for AI suggestions',
	},
	discardAll: {
		id: 'suggestions-panel.header.discard-all',
		defaultMessage: 'Discard all',
		description: 'Menu item to immediately discard all remaining AI suggestions',
	},
});

const useSuggestionsHeaderState = () => {
	const { fullPageEditorPresetAPI } = useFullPageEditorApiState();
	const state = useSharedPluginStateWithSelector(
		fullPageEditorPresetAPI,
		['aiSuggestions'],
		({ aiSuggestionsState }: { aiSuggestionsState?: AISuggestionsSharedState }) => ({
			count: aiSuggestionsState?.orderedSuggestionIds.length ?? 0,
			isThinking: aiSuggestionsState?.thinkingStatus === 'thinking',
		}),
	);
	return { ...state, api: fullPageEditorPresetAPI };
};

export const SuggestionsHeaderIcon = (): React.JSX.Element => <GenerativeIndicatorIcon label="" />;

export const SuggestionsCount = (): React.JSX.Element | null => {
	const { count } = useSuggestionsHeaderState();
	return count > 0 ? <Badge max={false}>{count}</Badge> : null;
};

export const SuggestionsHeaderActions = (): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { count, isThinking, api } = useSuggestionsHeaderState();
	const actions = api?.aiSuggestions?.actions;

	return (
		<DropdownMenu
			placement="bottom-end"
			trigger={({ triggerRef, ...triggerProps }) => (
				<IconButton
					{...triggerProps}
					ref={triggerRef}
					icon={ShowMoreHorizontalIcon}
					label={formatMessage(i18n.moreOptions)}
					appearance="subtle"
					spacing="default"
				/>
			)}
		>
			<DropdownItemGroup>
				<DropdownItem
					elemBefore={<TextReviewIcon label="" />}
					isDisabled={isThinking}
					onClick={() =>
						actions?.setThinkingState('thinking', { entryPoint: 'suggestionsPanelHeader' })
					}
				>
					{formatMessage(i18n.reviewAgain)}
				</DropdownItem>
				<DropdownItem
					elemBefore={<DeleteIcon label="" />}
					isDisabled={isThinking || count === 0}
					onClick={() => actions?.discardAllSuggestions()}
				>
					{formatMessage(i18n.discardAll)}
				</DropdownItem>
			</DropdownItemGroup>
		</DropdownMenu>
	);
};
